import { useMemo } from 'react';
import { Badge } from '@/components/ui/badge';
import { ConfidenceBadge } from '@/components/ui/confidence-badge';
import { SourceLinks } from '@/components/ui/source-links';
import { formatChiasmPattern } from './literary-context-utils';

interface MacroStructureCardProps {
  structure: {
    type: string;
    description: string;
    isPrimary?: boolean;
    chiasm?: {
      pattern: string;
      range?: string;
      confidence?: string;
    };
    sources?: Source[];
  };
}

/**
 * Shared component for displaying a single macro-structure
 * Used in both the book context page and the capability modal
 */
export function MacroStructureCard({ structure }: MacroStructureCardProps) {
  // Format chiasm if present
  const formattedChiasm = useMemo(() => {
    const isChiasm = structure.type.toLowerCase() === 'chiastic' && structure.chiasm?.pattern;
    return {
      isChiasm,
      chiasmElements: isChiasm ? formatChiasmPattern(structure.chiasm!.pattern) : []
    };
  }, [structure.type, structure.chiasm?.pattern]);

  return (
    <div className="text-sm">
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="flex items-center gap-2 flex-1">
          <h5 className="font-medium">
            {structure.type}
          </h5>
          {structure.chiasm?.confidence && typeof structure.chiasm.confidence === 'string' && (
            <ConfidenceBadge
              confidence={structure.chiasm.confidence}
              context="general"
            />
          )}
        </div>
        <div className="flex items-center gap-2">
          {structure.chiasm?.range && (
            <span className="text-xs text-muted-foreground">
              {structure.chiasm.range}
            </span>
          )}
          {structure.isPrimary && (
            <Badge variant="default">Primary</Badge>
          )}
        </div>
      </div>
      <p className="text-xs mb-2">{structure.description}</p>
      
      {/* Render chiasm pattern if present */}
      {structure.chiasm && formattedChiasm.isChiasm && (
        <div className="text-xs mt-3 space-y-1 ml-1">
          {formattedChiasm.chiasmElements.map((element, idx) => (
            <div
              key={idx}
              style={{ paddingLeft: `${element.level * 1.5}rem` }}
              className="text-muted-foreground"
            >
              <span className="font-medium text-foreground">{element.label}</span>
              {element.content && <span> {element.content}</span>}
            </div>
          ))}
        </div>
      )}

      {/* Sources */}
      {structure.sources && structure.sources.length > 0 && (
        <div className="mt-2">
          <SourceLinks sources={structure.sources} />
        </div>
      )}
    </div>
  );
}

interface MacroStructuresListProps {
  structures: MacroStructureCardProps['structure'][];
}

/**
 * List wrapper for multiple macro-structures
 */
export function MacroStructuresList({ structures }: MacroStructuresListProps) {
  if (!structures || structures.length === 0) return null;

  return (
    <div className="space-y-3">
      {structures.map((structure, index) => (
        <MacroStructureCard key={index} structure={structure} />
      ))}
    </div>
  );
}

